import { useEffect, useMemo } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BodyText, Button, HighlightWords, HStack, Modal, theme, useResponsive } from '@sg/garnish';
import { IngredientKind } from '@sg/graphql-schema';

import { type CustomizationMachineTypes } from '../../../machine';
import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationEncourageChangesModal = (props: Props) => {
  const {
    isVisible,
    entry,
    heading: headingOverride,
    baseHeading: baseHeadingOverride,
    noSelectionHeading: noSelectionHeadingOverride,
    body: bodyOverride,
    baseBody: baseBodyOverride,
    continueButtonTitle,
    goBackButtonTitle,
    onAddMore,
    onContinue,
    onShow,
    onRequestClose,
    size,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const formattedGroupName = entry.groupDisplayName.toLowerCase();
  const accessibilityLabel = formatMessage(messages.modalLabel, {
    group: formattedGroupName,
  });

  const isBasesGroup = entry.modificationIngredientKind === IngredientKind.Base;

  // ─── Messages ────────────────────────────────────────────────────────

  const heading = useMemo(() => {
    if (entry.currentSelection === 0) {
      return noSelectionHeadingOverride
        ? noSelectionHeadingOverride.replaceAll(DYNAMIC_GROUP_TEXT, formattedGroupName)
        : formatMessage(messages.headingNoSelection, { group: formattedGroupName });
    }

    if (isBasesGroup) {
      return baseHeadingOverride
        ? baseHeadingOverride
            .replaceAll(DYNAMIC_COUNT_TEXT, `${entry.currentSelection}`)
            .replaceAll(DYNAMIC_GROUP_TEXT, formattedGroupName)
        : formatMessage(messages.headingBases, {
            count: entry.currentSelection,
          });
    }

    return headingOverride
      ? headingOverride
          .replaceAll(DYNAMIC_COUNT_TEXT, `${entry.currentSelection}`)
          .replaceAll(DYNAMIC_GROUP_TEXT, formattedGroupName)
      : formatMessage(messages.heading, {
          count: entry.currentSelection,
          group: formattedGroupName,
        });
  }, [
    entry.currentSelection,
    isBasesGroup,
    formattedGroupName,
    headingOverride,
    baseHeadingOverride,
    noSelectionHeadingOverride,
    formatMessage,
  ]);

  const headingHighlights = useMemo(() => {
    const currentSelection = `${entry.currentSelection}`;

    if (isBasesGroup) {
      return [currentSelection, formatMessage(messages.base)];
    }

    return [currentSelection, formattedGroupName];
  }, [isBasesGroup, entry.currentSelection, formattedGroupName, formatMessage]);

  const body = useMemo(() => {
    if (isBasesGroup) {
      return baseBodyOverride
        ? baseBodyOverride.replaceAll(DYNAMIC_GROUP_TEXT, formattedGroupName)
        : formatMessage(messages.bodyBases);
    }

    return bodyOverride
      ? bodyOverride.replaceAll(DYNAMIC_GROUP_TEXT, formattedGroupName)
      : formatMessage(messages.body);
  }, [isBasesGroup, formattedGroupName, bodyOverride, baseBodyOverride, formatMessage]);

  const formattedContinueButtonTitle = continueButtonTitle ?? formatMessage(messages.continue);
  const formattedGoBackButtonTitle = goBackButtonTitle ?? formatMessage(messages.goBack);

  // ─── Styles ──────────────────────────────────────────────────────────

  const ctaStyles = size === 'lg' ? styles.ctaLG : match([styles.ctaXS, undefined]); // prettier-ignore

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="small"
      accessibilityLabel={accessibilityLabel}
      onRequestClose={onRequestClose}
    >
      <CustomizationModalContentContainer size={size ?? match(['md', 'lg'])}>
        <HighlightWords text={heading} highlights={headingHighlights} sizeMatch={['22']} />

        <BodyText sizeMatch={['22']}>{body}</BodyText>

        <SafeAreaView edges={match([['bottom'], undefined])}>
          <HStack itemsPerRow={2} gap={theme.spacing['4']} style={styles.ctaContainer}>
            <Button
              size="large-wide"
              palette="primary"
              onPress={onAddMore}
              accessibilityLabel={formattedGoBackButtonTitle}
              style={ctaStyles}
            >
              {formattedGoBackButtonTitle}
            </Button>

            <Button
              size="large-wide"
              palette="kale"
              onPress={onContinue}
              accessibilityLabel={formattedContinueButtonTitle}
              style={ctaStyles}
            >
              {formattedContinueButtonTitle}
            </Button>
          </HStack>
        </SafeAreaView>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly modalLabel: { readonly group: MessageValue };
  readonly heading: { readonly count: MessageValue; readonly group: MessageValue };
  readonly headingBases: { readonly count: MessageValue };
  readonly headingNoSelection: { readonly group: MessageValue };
  readonly body: NoMessageValues;
  readonly bodyBases: NoMessageValues;
  readonly base: NoMessageValues;
  readonly continue: NoMessageValues;
  readonly goBack: NoMessageValues;
}>({
  modalLabel: {
    defaultMessage: 'Encouraged changes for {group}',
    description: 'Customization | Encourage Changes | Modal label',
  },
  heading: {
    defaultMessage: 'Heads up — only {count} portion of {group} selected.',
    description: 'Customization | Encourage Changes | Heading',
  },
  headingBases: {
    defaultMessage: 'Heads up - only {count} base portion selected.',
    description: 'Customization | Encourage Changes | Heading base',
  },
  headingNoSelection: {
    defaultMessage: 'Heads up — no {group} selected.',
    description: 'Customization | Encourage Changes | Heading no selection',
  },
  body: {
    defaultMessage: 'Are you sure you’d like to continue?',
    description: 'Customization | Encourage Changes | Body',
  },
  bodyBases: {
    defaultMessage:
      'You can get a full portion by adding another base. Are you sure you’d like to continue?',
    description: 'Customization | Encourage Changes | Body base',
  },
  base: {
    defaultMessage: 'base',
    description: 'Customization | Encourage Changes | Base',
  },
  continue: {
    defaultMessage: 'Continue',
    description: 'Customization | Continue',
  },
  goBack: {
    defaultMessage: 'Go back',
    description: 'Customization | Go back',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DYNAMIC_GROUP_TEXT = '(group)';
const DYNAMIC_COUNT_TEXT = '(count)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    paddingTop: theme.spacing['2'],
    paddingBottom: Platform.select({
      android: theme.spacing['2'],
      default: 0,
    }),
  },
  ctaXS: {
    paddingHorizontal: theme.spacing['2'],
  },
  ctaLG: {
    minHeight: 72,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  entry: CustomizationMachineTypes.EncouragedChangeEntry;
  heading?: string;
  baseHeading?: string;
  noSelectionHeading?: string;
  body?: string;
  baseBody?: string;
  continueButtonTitle?: string;
  goBackButtonTitle?: string;
  onAddMore: () => void;
  onContinue: () => void;
  onRequestClose?: () => void;
  onShow?: () => void;
  size?: 'md' | 'lg';
};
